import { Routes, Route, useNavigate } from 'react-router';
import { useEffect, useRef, lazy, Suspense } from 'react';
import { Toaster } from '@/components/ui/sonner';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { initSecurity } from '@/lib/security';
import { TRPCProvider } from '@/providers/trpc';
import { SessionProvider } from '@/context/SessionContext';
import { AuthGuard } from '@/components/AuthGuard';
import { useAuth } from '@/hooks/useAuth';
import DashboardLayout from '@/sections/DashboardLayout';
import DashboardPage from '@/pages/DashboardPage';
import ParametersPage from "@/pages/ParametersPage";
import TrendsPage from '@/pages/TrendsPage';
import ReportsPage from '@/pages/ReportsPage';
import CustomersPage from '@/pages/CustomersPage';
import SamplesPage from '@/pages/SamplesPage';
import StandardsPage from '@/pages/StandardsPage';
import DosingPage from '@/pages/DosingPage';
import ProcessDatabasePage from '@/pages/ProcessDatabasePage';
import Login from '@/pages/Login';
import AdminDashboard from '@/pages/AdminDashboard';
import DemoDataAdminPage from '@/pages/DemoDataAdminPage';
import InstructionsPage from '@/pages/InstructionsPage';
import UserProfilePage from '@/pages/UserProfilePage';
import PasswordResetPage from '@/pages/PasswordResetPage';
import BackendSetupPage from '@/pages/BackendSetupPage';
import InfluentPage from '@/pages/InfluentPage';
import InfluentEntryPage from '@/pages/InfluentEntryPage';
import EffluentPage from '@/pages/EffluentPage';
import EffluentEntryPage from '@/pages/EffluentEntryPage';
const DAFSpecPage = lazy(() => import('@/pages/DAFSpecPage'));
import AdminDAFValidation from '@/pages/AdminDAFValidation';
import AdminLamellaValidation from '@/pages/AdminLamellaValidation';
import PumpListPage from '@/pages/PumpListPage';
import PumpDetailPage from '@/pages/PumpDetailPage';
import AdvertPage from '@/pages/AdvertPage';
const LamellaSpecPage = lazy(() => import('@/pages/LamellaSpecPage'));
import AdminPHValidation from '@/pages/AdminPHValidation';
import AdminAerationValidation from '@/pages/AdminAerationValidation';
import AdminMultimediaValidation from '@/pages/AdminMultimediaValidation';
import AdminSludgeValidation from '@/pages/AdminSludgeValidation';
import AdminSecondaryValidation from '@/pages/AdminSecondaryValidation';
import AdminBufferValidation from '@/pages/AdminBufferValidation';
import GACSpecPage from '@/pages/GACSpecPage';
import AdminGACValidation from '@/pages/AdminGACValidation';
import MultimediaSpecPage from '@/pages/MultimediaSpecPage';
import AerationColumnSpecPage from '@/pages/AerationColumnSpecPage';
import GreensandSpecPage from '@/pages/GreensandSpecPage';
import UFSpecPage from '@/pages/UFSpecPage';
import UVDisinfectionSpecPage from '@/pages/UVDisinfectionSpecPage';
import MylarBoreholeAssessmentPage from '@/pages/MylarBoreholeAssessmentPage';
import LabWorksheetPage from '@/pages/LabWorksheetPage';
import TrainEditPage from '@/pages/TrainEditPage';
import EQSpecPage from '@/pages/EQSpecPage';
import AdminEQValidation from '@/pages/AdminEQValidation';
import PHSpecPage from '@/pages/PHSpecPage';
import AerationSpecPage from '@/pages/AerationSpecPage';
import SecondarySpecPage from '@/pages/SecondarySpecPage';
import BufferSpecPage from '@/pages/BufferSpecPage';
import SludgeHoldingSpecPage from '@/pages/SludgeHoldingSpecPage';
import EquipmentValidationHub from '@/pages/EquipmentValidationHub';
import EquipmentListPage from '@/pages/EquipmentListPage';
import TrendReportsPage from '@/pages/TrendReportsPage';
import InfluentTrendsPage from '@/pages/InfluentTrendsPage';
import EffluentTrendsPage from '@/pages/EffluentTrendsPage';
import ProcessTrainPage from '@/pages/ProcessTrainPage';
import ProposalPage from '@/pages/ProposalPage';
import ComplianceGatewayPage from '@/pages/ComplianceGatewayPage';
import SAWaterChargePage from '@/pages/SAWaterChargePage';
import SampleWorkflowPage from '@/pages/SampleWorkflowPage';
import EffluentReportsPage from '@/pages/EffluentReportsPage';
import SiteAssessmentPage from '@/pages/SiteAssessmentPage';
import CascadeAeratorSpecPage from '@/pages/CascadeAeratorSpecPage';
import AmmoniaStripperSpecPage from '@/pages/AmmoniaStripperSpecPage';
import ChemicalDosingPage from '@/pages/ChemicalDosingPage';
import ImpellerMixingPage from '@/pages/ImpellerMixingPage';
import DosingMixingTrainPage from '@/pages/DosingMixingTrainPage';
import ReservoirManagementPage from '@/pages/ReservoirManagementPage';
import ReservoirAerationSpecPage from '@/pages/ReservoirAerationSpecPage';
import DiffuserModulePage from '@/pages/DiffuserModulePage';
import ModuleArrayPage from '@/pages/ModuleArrayPage';
import DiffuserValidationReportPage from '@/pages/DiffuserValidationReportPage';
import CustomDiffuserPage from '@/pages/CustomDiffuserPage';
import ReservoirLayoutPage from '@/pages/ReservoirLayoutPage';
import ReservoirValidationReportPage from '@/pages/ReservoirValidationReportPage';
import ReservoirCalculationStepsPage from '@/pages/ReservoirCalculationStepsPage';
import SedimentBedPhysicsPage from '@/pages/SedimentBedPhysicsPage';
import ReservoirMasterReportPage from '@/pages/ReservoirMasterReportPage';
import ReservoirBedAssessmentPage from '@/pages/ReservoirBedAssessmentPage';
import PlumeCoverageMapPage from '@/pages/PlumeCoverageMapPage';
import PoreReleaseGeometryPage from '@/pages/PoreReleaseGeometryPage';
import DiffuserComparisonPage from '@/pages/DiffuserComparisonPage';
import RiskRegisterPage from '@/pages/RiskRegisterPage';
import ReservoirComplianceGatewayPage from '@/pages/ReservoirComplianceGatewayPage';
import SiteDataCapturePage from '@/pages/SiteDataCapturePage';
import ThermalModelPage from '@/pages/ThermalModelPage';
import AlgalDynamicsPage from '@/pages/AlgalDynamicsPage';
import GHGBalancePage from '@/pages/GHGBalancePage';
import SensitivityTornadoPage from '@/pages/SensitivityTornadoPage';
import BlowerSelectionPage from '@/pages/BlowerSelectionPage';
import BathymetryImportPage from '@/pages/BathymetryImportPage';
import PilotTrialPlannerPage from '@/pages/PilotTrialPlannerPage';
import FoulingDriftPage from '@/pages/FoulingDriftPage';
import OMSchedulePage from '@/pages/OMSchedulePage';
import AcousticNoisePage from '@/pages/AcousticNoisePage';
import CitationManagerPage from '@/pages/CitationManagerPage';
import PDFIndexPage from '@/pages/PDFIndexPage';
import CalculationChainPage from '@/pages/CalculationChainPage';
import ReservoirHubPage from '@/pages/ReservoirHubPage';
import SamplingPlanPage from '@/pages/SamplingPlanPage';
import ValidationStepsPage from '@/pages/ValidationStepsPage';
import WaterQualityPredictorPage from '@/pages/WaterQualityPredictorPage';
import SimulationDataPage from '@/pages/SimulationDataPage';
import PipeFlowCalculatorPage from '@/pages/PipeFlowCalculatorPage';
import CustomDiffuserSystemPage from '@/pages/CustomDiffuserSystemPage';
import ReservoirDataPackPage from '@/pages/ReservoirDataPackPage';
import ModulesHubPage from '@/pages/ModulesHubPage';
import AsaedaImbergerPage from '@/pages/modules/AsaedaImbergerPage';
import DeepZoneWeightingPage from '@/pages/modules/DeepZoneWeightingPage';
import WindTiltPage from '@/pages/modules/WindTiltPage';
import ResuspensionPenaltyPage from '@/pages/modules/ResuspensionPenaltyPage';
import CostPerO2Page from '@/pages/modules/CostPerO2Page';
import DDSOptimiserPage from '@/pages/modules/DDSOptimiserPage';
import NSGA2Page from '@/pages/modules/NSGA2Page';
import GLMCouplingPage from '@/pages/modules/GLMCouplingPage';
import ELCOMCouplingPage from '@/pages/modules/ELCOMCouplingPage';
import LangmuirCirculationPage from '@/pages/modules/LangmuirCirculationPage';
import CoalescencePage from '@/pages/modules/CoalescencePage';
import HigbieKLPage from '@/pages/modules/HigbieKLPage';
import CSRGroupsPage from '@/pages/modules/CSRGroupsPage';
import BoudreauDiagenesisPage from '@/pages/modules/BoudreauDiagenesisPage';
import InternalSeichePage from '@/pages/modules/InternalSeichePage';
import ReckhowPRetentionPage from '@/pages/modules/ReckhowPRetentionPage';
import KumagaiFeedbackPage from '@/pages/modules/KumagaiFeedbackPage';
import NomSodPage from '@/pages/modules/NomSodPage';
import RedoxPReleasePage from '@/pages/modules/RedoxPReleasePage';
import GeosminPredictorPage from '@/pages/modules/GeosminPredictorPage';
import SedimentCappingPage from '@/pages/modules/SedimentCappingPage';
import NPColimitationPage from '@/pages/modules/NPColimitationPage';
import WuestPlumePage from '@/pages/modules/WuestPlumePage';
import McGinnisBubblePage from '@/pages/modules/McGinnisBubblePage';
import MehtaPartheniadesPage from '@/pages/modules/MehtaPartheniadesPage';
import AllerBioturbationPage from '@/pages/modules/AllerBioturbationPage';
import DavisonFeMnPage from '@/pages/modules/DavisonFeMnPage';
import MacIntyreConvectionPage from '@/pages/modules/MacIntyreConvectionPage';
import EpilimneticTargetedMixingPage from '@/pages/modules/EpilimneticTargetedMixingPage';
import SeasonalSchedulerPage from '@/pages/modules/SeasonalSchedulerPage';
import YearRoundStrategyHubPage from '@/pages/scenarios/YearRoundStrategyHubPage';
import StrategyContinuousFullPage from '@/pages/scenarios/StrategyContinuousFullPage';
import StrategySummerFullMixPage from '@/pages/scenarios/StrategySummerFullMixPage';
const StrategyAdaptiveSchedulerPage = lazy(() => import('@/pages/scenarios/StrategyAdaptiveSchedulerPage'));
import StrategyTemOnlySummerPage from '@/pages/scenarios/StrategyTemOnlySummerPage';
const StrategyCDefensibilityPage = lazy(() => import('@/pages/scenarios/StrategyCDefensibilityPage'));
import ModuleTrainsGuidePage from '@/pages/ModuleTrainsGuidePage';
import TrainSummaryPage from '@/pages/TrainSummaryPage';
import BenchmarkSitesPage from '@/pages/BenchmarkSitesPage';
import AerationKnowledgeBasePage from '@/pages/AerationKnowledgeBasePage';
import PerfectScenarioPage from '@/pages/PerfectScenarioPage';
import ScenarioManagerPage from '@/pages/ScenarioManagerPage';
import DefensibilityStatementPage from '@/pages/DefensibilityStatementPage';
import PerfectDiffuserPage from '@/pages/PerfectDiffuserPage';
import AerationConfigPage from '@/pages/AerationConfigPage';
import PackedColumnStripperPage from '@/pages/PackedColumnStripperPage';
const DesalinationDAFSpecPage = lazy(() => import('@/pages/DesalinationDAFSpecPage'));
const OrificeSpecPage = lazy(() => import('@/pages/OrificeSpecPage'));
import ElectrochlorinationSpecPage from '@/pages/ElectrochlorinationSpecPage';
import InfluentStudyPage from '@/pages/InfluentStudyPage';
import EffluentStudyPage from '@/pages/EffluentStudyPage';
import CustomerSitePage from '@/pages/CustomerSitePage';
import ComplianceDashboardPage from '@/pages/ComplianceDashboardPage';
import NotFound from '@/pages/NotFound';
import { ProposalProvider } from '@/context/ProposalContext';

/**
 * Initialize client-side security protection (devtools detection, etc.)
 * This is a DETERRENT — a determined user with technical knowledge can bypass it.
 * Real security comes from server-side .htaccess rules and auth.
 */
function SecurityInit() {
  useEffect(() => {
    // DISABLED for debugging — re-enable after fixing auth issues
    // if (import.meta.env.PROD) { initSecurity(); }
  }, []);
  return null;
}

function PostLoginRedirect() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const wasAuthenticatedRef = useRef(isAuthenticated);

  useEffect(() => {
    // Only redirect when transitioning from unauthenticated → authenticated
    // (i.e. right after login), NOT on every mount when already authenticated.
    if (isAuthenticated && !wasAuthenticatedRef.current) {
      wasAuthenticatedRef.current = true;
      const redirect = sessionStorage.getItem('rb_redirect_after_login');
      if (redirect) {
        sessionStorage.removeItem('rb_redirect_after_login');
        navigate(redirect, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
    if (!isAuthenticated) {
      wasAuthenticatedRef.current = false;
    }
  }, [isAuthenticated, navigate]);

  return null;
}

export default function App() {
  return (
    <>
      <SecurityInit />
      <Toaster position="top-right" richColors />
      <ErrorBoundary>
        <TRPCProvider>
          <SessionProvider>
          <ProposalProvider>
            <Suspense fallback={<div className="flex items-center justify-center h-screen text-slate-400 text-sm">Loading...</div>}>
            <Routes>
              {/* Login — outside DashboardLayout, no sidebar, no auth required */}
              <Route path="/login" element={<Login />} />

              {/* Protected routes — require auth + show sidebar */}
              <Route element={<AuthGuard><DashboardLayout /></AuthGuard>}>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/parameters" element={<ParametersPage />} />
                <Route path="/trends" element={<TrendsPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/customers" element={<CustomersPage />} />
                <Route path="/samples" element={<SamplesPage />} />
                <Route path="/standards" element={<StandardsPage />} />
                <Route path="/dosing" element={<DosingPage />} />
                <Route path="/chemical-dosing" element={<ChemicalDosingPage />} />
                <Route path="/impeller-mixing" element={<ImpellerMixingPage />} />
                <Route path="/dosing-mixing-train" element={<DosingMixingTrainPage />} />
                <Route path="/reservoir-management" element={<ReservoirManagementPage />} />
                <Route path="/reservoir-aeration-spec" element={<ReservoirAerationSpecPage />} />
                <Route path="/diffuser-module" element={<DiffuserModulePage />} />
                <Route path="/module-array" element={<ModuleArrayPage />} />
                <Route path="/diffuser-validation" element={<DiffuserValidationReportPage />} />
                <Route path="/custom-diffuser" element={<CustomDiffuserPage />} />
                <Route path="/reservoir-layout" element={<ReservoirLayoutPage />} />
                <Route path="/reservoir-validation" element={<ReservoirValidationReportPage />} />
                <Route path="/reservoir-steps" element={<ReservoirCalculationStepsPage />} />
                <Route path="/sediment-bed-physics" element={<SedimentBedPhysicsPage />} />
                <Route path="/reservoir-master" element={<ReservoirMasterReportPage />} />
                <Route path="/bed-assessment" element={<ReservoirBedAssessmentPage />} />
                <Route path="/plume-coverage" element={<PlumeCoverageMapPage />} />
                <Route path="/pore-release-geometry" element={<PoreReleaseGeometryPage />} />
                <Route path="/diffuser-comparison" element={<DiffuserComparisonPage />} />
                <Route path="/risk-register" element={<RiskRegisterPage />} />
                <Route path="/reservoir-compliance" element={<ReservoirComplianceGatewayPage />} />
                <Route path="/site-data-capture" element={<SiteDataCapturePage />} />
                <Route path="/thermal-model" element={<ThermalModelPage />} />
                <Route path="/algal-dynamics" element={<AlgalDynamicsPage />} />
                <Route path="/ghg-balance" element={<GHGBalancePage />} />
                <Route path="/sensitivity" element={<SensitivityTornadoPage />} />
                <Route path="/blower-selection" element={<BlowerSelectionPage />} />
                <Route path="/bathymetry-import" element={<BathymetryImportPage />} />
                <Route path="/pilot-trial-plan" element={<PilotTrialPlannerPage />} />
                <Route path="/fouling-drift" element={<FoulingDriftPage />} />
                <Route path="/om-schedule" element={<OMSchedulePage />} />
                <Route path="/acoustic-noise" element={<AcousticNoisePage />} />
                <Route path="/citations" element={<CitationManagerPage />} />
                <Route path="/pdf-index" element={<PDFIndexPage />} />
                <Route path="/calculation-chain" element={<CalculationChainPage />} />
                <Route path="/reservoir-hub" element={<ReservoirHubPage />} />
                <Route path="/sampling-plan" element={<SamplingPlanPage />} />
                <Route path="/validation-steps" element={<ValidationStepsPage />} />
                <Route path="/water-quality-predictor" element={<WaterQualityPredictorPage />} />
                <Route path="/simulation-data" element={<SimulationDataPage />} />
                <Route path="/pipe-flow-calculator" element={<PipeFlowCalculatorPage />} />
                <Route path="/custom-diffuser-system" element={<CustomDiffuserSystemPage />} />
                <Route path="/reservoir-data-pack" element={<ReservoirDataPackPage />} />
                <Route path="/modules" element={<ModulesHubPage />} />
                <Route path="/module/asaeda-imberger" element={<AsaedaImbergerPage />} />
                <Route path="/module/deep-zone-weighting" element={<DeepZoneWeightingPage />} />
                <Route path="/module/wind-tilt" element={<WindTiltPage />} />
                <Route path="/module/resuspension-penalty" element={<ResuspensionPenaltyPage />} />
                <Route path="/module/cost-per-o2" element={<CostPerO2Page />} />
                <Route path="/module/dds-optimiser" element={<DDSOptimiserPage />} />
                <Route path="/module/nsga2" element={<NSGA2Page />} />
                <Route path="/module/glm-coupling" element={<GLMCouplingPage />} />
                <Route path="/module/elcom-coupling" element={<ELCOMCouplingPage />} />
                <Route path="/module/langmuir" element={<LangmuirCirculationPage />} />
                <Route path="/module/coalescence" element={<CoalescencePage />} />
                <Route path="/module/higbie-kl" element={<HigbieKLPage />} />
                <Route path="/module/csr-groups" element={<CSRGroupsPage />} />
                <Route path="/module/boudreau-diagenesis" element={<BoudreauDiagenesisPage />} />
                <Route path="/module/internal-seiche" element={<InternalSeichePage />} />
                <Route path="/module/reckhow-p" element={<ReckhowPRetentionPage />} />
                <Route path="/module/kumagai-feedback" element={<KumagaiFeedbackPage />} />
                <Route path="/module/nom-sod" element={<NomSodPage />} />
                <Route path="/module/redox-p-release" element={<RedoxPReleasePage />} />
                <Route path="/module/geosmin-predictor" element={<GeosminPredictorPage />} />
                <Route path="/module/sediment-capping" element={<SedimentCappingPage />} />
                <Route path="/module/np-colimitation" element={<NPColimitationPage />} />
                <Route path="/module/wuest-plume" element={<WuestPlumePage />} />
                <Route path="/module/mcginnis-bubble" element={<McGinnisBubblePage />} />
                <Route path="/module/mehta-partheniades" element={<MehtaPartheniadesPage />} />
                <Route path="/module/aller-bioturbation" element={<AllerBioturbationPage />} />
                <Route path="/module/davison-fe-mn" element={<DavisonFeMnPage />} />
                <Route path="/module/macintyre-convection" element={<MacIntyreConvectionPage />} />
                <Route path="/module/tem-epilimnetic" element={<EpilimneticTargetedMixingPage />} />
                <Route path="/module/seasonal-scheduler" element={<SeasonalSchedulerPage />} />
                <Route path="/scenarios/year-round" element={<YearRoundStrategyHubPage />} />
                <Route path="/scenarios/year-round/strategy-a-continuous"  element={<StrategyContinuousFullPage />} />
                <Route path="/scenarios/year-round/strategy-b-summer-fullmix" element={<StrategySummerFullMixPage />} />
                <Route path="/scenarios/year-round/strategy-c-adaptive"    element={<StrategyAdaptiveSchedulerPage />} />
                <Route path="/scenarios/year-round/strategy-d-tem-only"    element={<StrategyTemOnlySummerPage />} />
                <Route path="/scenarios/year-round/strategy-c-defensibility" element={<StrategyCDefensibilityPage />} />
                <Route path="/module-trains" element={<ModuleTrainsGuidePage />} />
                <Route path="/train-summary/:id" element={<TrainSummaryPage />} />
                <Route path="/benchmark-sites" element={<BenchmarkSitesPage />} />
                <Route path="/aeration-knowledge" element={<AerationKnowledgeBasePage />} />
                <Route path="/perfect-scenario" element={<PerfectScenarioPage />} />
                <Route path="/scenarios" element={<ScenarioManagerPage />} />
                <Route path="/defensibility" element={<DefensibilityStatementPage />} />
                <Route path="/perfect-diffuser" element={<PerfectDiffuserPage />} />
                <Route path="/aeration-config" element={<AerationConfigPage />} />
                <Route path="/site-assessment" element={<SiteAssessmentPage />} />
                <Route path="/process-db" element={<ProcessDatabasePage />} />
                <Route path="/profile" element={<UserProfilePage />} />
                <Route path="/password" element={<PasswordResetPage />} />
                <Route path="/backend-setup" element={<BackendSetupPage />} />
                <Route path="/trend-reports" element={<TrendReportsPage />} />
                <Route path="/influent-trends" element={<InfluentTrendsPage />} />
                <Route path="/effluent-trends" element={<EffluentTrendsPage />} />
                <Route path="/influent" element={<InfluentPage />} />
                <Route path="/influent-entry" element={<InfluentEntryPage />} />
                <Route path="/effluent" element={<EffluentPage />} />
                <Route path="/effluent-entry" element={<EffluentEntryPage />} />
                <Route path="/daf-spec" element={<DAFSpecPage />} />
                <Route path="/pumps" element={<PumpListPage />} />
                <Route path="/pumps/:id" element={<PumpDetailPage />} />
                <Route path="/advert" element={<AdvertPage />} />
                <Route path="/lamella-spec" element={<LamellaSpecPage />} />
                <Route path="/admin/lamella-validation" element={<AdminLamellaValidation />} />
                <Route path="/admin/ph-validation" element={<AdminPHValidation />} />
                <Route path="/admin/aeration-validation" element={<AdminAerationValidation />} />
                <Route path="/admin/secondary-validation" element={<AdminSecondaryValidation />} />
                <Route path="/admin/buffer-validation" element={<AdminBufferValidation />} />
                <Route path="/gac-spec" element={<GACSpecPage />} />
                <Route path="/admin/gac-validation" element={<AdminGACValidation />} />
                <Route path="/multimedia-spec" element={<MultimediaSpecPage />} />
                <Route path="/aeration-column-spec" element={<AerationColumnSpecPage />} />
                <Route path="/greensand-spec" element={<GreensandSpecPage />} />
                <Route path="/uf-spec" element={<UFSpecPage />} />
                <Route path="/uv-spec" element={<UVDisinfectionSpecPage />} />
                <Route path="/mylar-borehole-assessment" element={<MylarBoreholeAssessmentPage />} />
                <Route path="/lab-worksheet" element={<LabWorksheetPage />} />
                <Route path="/train-edit" element={<TrainEditPage />} />
                <Route path="/admin/multimedia-validation" element={<AdminMultimediaValidation />} />
                <Route path="/eq-spec" element={<EQSpecPage />} />
                <Route path="/admin/eq-validation" element={<AdminEQValidation />} />
                <Route path="/ph-spec" element={<PHSpecPage />} />
                <Route path="/aeration-spec" element={<AerationSpecPage />} />
                <Route path="/cascade-aerator-spec" element={<CascadeAeratorSpecPage />} />
                <Route path="/ammonia-stripper-spec" element={<AmmoniaStripperSpecPage />} />
                <Route path="/packed-column-stripper" element={<PackedColumnStripperPage />} />
                <Route path="/desalination-daf-spec" element={<DesalinationDAFSpecPage />} />
                <Route path="/electrochlorination-spec" element={<ElectrochlorinationSpecPage />} />
                <Route path="/orifice-spec" element={<OrificeSpecPage />} />
                <Route path="/influent-study" element={<InfluentStudyPage />} />
                <Route path="/effluent-study" element={<EffluentStudyPage />} />
                <Route path="/customer-site" element={<CustomerSitePage />} />
                <Route path="/compliance-dashboard" element={<ComplianceDashboardPage />} />
                <Route path="/secondary-spec" element={<SecondarySpecPage />} />
                <Route path="/buffer-spec" element={<BufferSpecPage />} />
                <Route path="/sludge-spec" element={<SludgeHoldingSpecPage />} />
                <Route path="/admin/sludge-validation" element={<AdminSludgeValidation />} />
                <Route path="/equipment-validation" element={<EquipmentValidationHub />} />
                <Route path="/equipment-list" element={<EquipmentListPage />} />
                <Route path="/process-train" element={<ProcessTrainPage />} />
                <Route path="/proposals" element={<ProposalPage />} />
                <Route path="/compliance-gateway" element={<ComplianceGatewayPage />} />
                <Route path="/sample-workflow" element={<SampleWorkflowPage />} />
                <Route path="/effluent-reports" element={<EffluentReportsPage />} />
                <Route path="/sa-water-charges" element={<SAWaterChargePage />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/demo-data" element={<DemoDataAdminPage />} />
                <Route path="/admin/daf-validation" element={<AdminDAFValidation />} />
                <Route path="/instructions" element={<InstructionsPage />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
            </Suspense>
            <PostLoginRedirect />
          </ProposalProvider>
          </SessionProvider>
        </TRPCProvider>
      </ErrorBoundary>
    </>
  );
}
